import {
  Button,
  Card,
  Divider,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableHead,
  Typography,
  IconButton,
  Alert as MuiAlert,
  Pagination,
  CardContent,
  TableContainer,
  TextField,
  Box
} from '@mui/material'
import { Add as AddIcon, Delete } from '@mui/icons-material'
import EditIcon from '@mui/icons-material/Edit'
import { useEffect, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck } from '../../utils/utils'
import ContactPersonDrawer from '../contact_person/contactperson/contactPersonDialog'
import {
  ConfirmationDialog,
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow
} from '../../utils/ui_utils'
import {
  useCreateContactPerson,
  useFetchContactPerson,
  useSearchContactPerson,
  useUpdateContactPerson
} from '../../modules/contactPerson/contactPersonHooks'

function ContactPerson() {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [pagecount, setPageCount] = useState(1)
  const [goToPageInput, setGoToPageInput] = useState('')
  const [contactPersonData, setContactPersonData] = useState(null)

  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)

  const [contactPersonDrawerMode, setContactPersonDrawerMode] = useState('edit')
  const [contactPersonDrawerVisibilty, setContactPersonDrawerVisibility] =
    useState(false)
  const [deleteConfirmationVisibility, setDeleteConfirmationVisibility] =
    useState(false)

  const {
    fetchContactPerson,
    fetchContactPersonData,
    fetchContactPersonError,
    fetchContactPersonLoading
  } = useFetchContactPerson()
  const {
    searchContactPerson,
    searchContactPersonData,
    searchContactPersonError,
    searchContactPersonLoading
  } = useSearchContactPerson()
  const { createContactPerson } = useCreateContactPerson()
  const { updateContactPerson } = useUpdateContactPerson()

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  })

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const pageCount = (data1) => {
    let count = Math.ceil(data1 / limit)
    setPageCount(count)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    let payload = {
      pageNumber: value,
      limit: limit
    }
    fetchContactPerson(payload)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (/^\d+$/.test(value) || value === '') {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNum = parseInt(goToPageInput, 10)
    if (pageNum >= 1 && pageNum <= pagecount) {
      handlePageChange(null, pageNum)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const history = useNavigate()

  useEffect(() => {
    businessCheck(history)
    let payload = {
      pageNumber: page,
      limit: limit
    }
    fetchContactPerson(payload)
  }, [])

  useEffect(() => {
    if (fetchContactPersonData.data?.length > 0) {
      pageCount(fetchContactPersonData.total)
    }
  }, [fetchContactPersonData])

  if (fetchContactPersonError || searchContactPersonError) return <p>Error!</p>
  if (fetchContactPersonLoading || searchContactPersonLoading)
    return <p>Loading...</p>
  if (!fetchContactPersonLoading || !searchContactPersonLoading) {
    return (
      <>
        <Helmet title="Contact Person" />
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Grid item xs={9} md={6}>
            <Typography variant="h4" gutterBottom display="inline">
              Contact Person
            </Typography>
          </Grid>
          <Grid item>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              color="primary"
              onClick={() => {
                setContactPersonDrawerVisibility(true)
                setContactPersonDrawerMode('new')
                setContactPersonData({
                  first_name: '',
                  last_name: '',
                  mobile: '',
                  email: '',
                  telephone: '',
                  comments: ''
                })
              }}
            >
              Add Contact
            </Button>
            <ContactPersonDrawer
              visible={contactPersonDrawerVisibilty}
              title={
                contactPersonDrawerMode == 'new'
                  ? 'Create Contact Person'
                  : 'Edit Contact Person'
              }
              okText={contactPersonDrawerMode == 'new' ? 'Create' : 'Update'}
              initialValues={contactPersonData}
              onClose={() => setContactPersonDrawerVisibility(false)}
              onSubmit={async (value) => {
                let values = _.cloneDeep(value)
                if (contactPersonDrawerMode == 'new') {
                  if (values.last_name == '') delete values.last_name
                  if (values.telephone == '') {
                    delete values.telephone
                  }
                  if (values.comments == '') {
                    delete values.comments
                  }
                  await createContactPerson(values, (success, error) => {
                    if (error) {
                      setContactPersonDrawerVisibility(true)
                      toastMessage('Error occurred', 'error')
                    } else {
                      setContactPersonDrawerVisibility(false)
                      toastMessage('Contact Person created', 'success')
                      let payload = {
                        pageNumber: page,
                        limit: limit
                      }
                      fetchContactPerson(payload)
                    }
                  })
                } else {
                  delete values.business_id
                  values.id = values._id
                  delete values._id
                  delete values.key
                  if (values.last_name == '') delete values.last_name
                  if (values.telephone == '') {
                    delete values.telephone
                  }
                  if (values.comments == '') {
                    delete values.comments
                  }

                  await updateContactPerson(values, (success, error, msg) => {
                    if (error) {
                      setContactPersonDrawerVisibility(true)
                      toastMessage(error.msg, 'error')
                    } else {
                      setContactPersonDrawerVisibility(false)
                      toastMessage('Contact Person updated', 'success')
                      let payload = {
                        pageNumber: page,
                        limit: limit
                      }
                      fetchContactPerson(payload)
                    }
                  })
                }
              }}
            />
          </Grid>
        </Grid>
        <Divider sx={{ marginTop: 5 }} />
        <Grid container spacing={10} sx={{ paddingTop: 5 }}>
          <Grid item md={8} lg={10}>
            <Card raised sx={{ borderRadius: 3 }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <TableContainer component={Card}>
                  <Table size="small">
                    <TableHead>
                      <StyledTableRow>
                        <StyledTableCell>First Name</StyledTableCell>
                        <StyledTableCell>Last Name</StyledTableCell>
                        <StyledTableCell> Mobile</StyledTableCell>
                        <StyledTableCell>Email</StyledTableCell>
                        <StyledTableCell>Telephone</StyledTableCell>
                        <StyledTableCell>Comments</StyledTableCell>
                        <StyledTableCell align="center">Edit</StyledTableCell>
                      </StyledTableRow>
                    </TableHead>
                    <TableBody>
                      {fetchContactPersonData.data?.map((row, i) => (
                        <StyledTableRow key={i} hover>
                          <StyledTableCell component="th" scope="row">
                            {row.first_name}
                          </StyledTableCell>
                          <StyledTableCell>{row.last_name}</StyledTableCell>
                          <StyledTableCell>{row.mobile}</StyledTableCell>
                          <StyledTableCell>{row.email}</StyledTableCell>
                          <StyledTableCell>{row.telephone}</StyledTableCell>
                          <StyledTableCell>{row.comments}</StyledTableCell>
                          <StyledTableCell align="center">
                            <IconButton
                              size="small"
                              color="primary"
                              component="span"
                              onClick={() => {
                                setContactPersonDrawerVisibility(true)
                                setContactPersonDrawerMode('edit')
                                setContactPersonData(row)
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {/* <IconButton
                              size="small"
                              color="primary"
                              component="span"
                              onClick={() => {
                                setContactPersonInfoDrawerVisibility(true)
                                setContactPersonData(row)
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton> */}
                            <IconButton
                              size="small"
                              color="error"
                              component="span"
                              onClick={() => {
                                setContactPersonData(row)
                                setDeleteConfirmationVisibility(true)
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
              {pagecount > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%'
                    }}
                  >
                    <Pagination
                      count={pagecount}
                      page={page}
                      color="secondary"
                      size="small"
                      boundaryCount={2}
                      siblingCount={0}
                      showFirstButton
                      showLastButton
                      onChange={handlePageChange}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          transition: 'all 0.2s ease-in-out'
                        }
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 1.5,
                      backgroundColor: 'white',
                      p: 2,
                      borderRadius: 1,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      transition: 'box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                    >
                      Jump to
                    </Typography>
                    <TextField
                      size="small"
                      value={goToPageInput}
                      onChange={handleGoToPageChange}
                      onKeyPress={handleGoToPageKeyPress}
                      placeholder="Page"
                      sx={{
                        width: '80px',
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          transition: 'all 0.2s ease-in-out',
                          '& fieldset': {
                            borderColor: 'grey.300',
                            borderWidth: '1px'
                          },
                          '&:hover fieldset': {
                            borderColor: 'secondary.main',
                            borderWidth: '1.5px'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'secondary.main',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 3px rgba(156, 39, 176, 0.1)'
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          textAlign: 'center',
                          fontWeight: 500,
                          padding: '7px 8px',
                          height: '20px',
                          lineHeight: '20px'
                        }
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', fontWeight: 400 }}
                    >
                      of {pagecount}
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={handleGoToPageSubmit}
                      disabled={
                        !goToPageInput ||
                        parseInt(goToPageInput, 10) < 1 ||
                        parseInt(goToPageInput, 10) > pagecount
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        height: '34px',
                        lineHeight: 1,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)'
                        },
                        '&:active': {
                          transform: 'translateY(0)'
                        }
                      }}
                    >
                      Go
                    </Button>
                  </Box>
                </Box>
              )}
            </Card>
            {/* <ContactPersonInfoDrawer
              visible={contactPersonInfoDrawerVisibility}
              title={"Customer Person Info"}
              closeText={"Close"}
              initialValues={contactPersonData}
              onClose={() => setContactPersonInfoDrawerVisibility(false)} /> */}
          </Grid>
        </Grid>
        <div>
          <ConfirmationDialog
            visibility={deleteConfirmationVisibility}
            title={'Delete Contact Details'}
            message={`Do you want to delete details of Contact Person - ${contactPersonData?.first_name} ?`}
            okText="Delete"
            cancelText={'Cancel'}
            onCancel={() => {
              setDeleteConfirmationVisibility(false)
            }}
            onOk={async () => {
              let values = _.cloneDeep(contactPersonData)
              delete values.business_id
              values.id = values._id
              delete values.key
              delete values._id
              if (values.last_name == '') delete values.last_name
              if (values.telephone == '') {
                delete values.telephone
              }
              if (values.comments == '') {
                delete values.comments
              }
              values.delete_status = true
              await updateContactPerson(values, (success, error, message) => {
                if (error) {
                  setDeleteConfirmationVisibility(false)
                  toastMessage(message, 'error')
                } else {
                  setDeleteConfirmationVisibility(false)
                  toastMessage('Contact Person deleted', 'success')
                  let payload = {
                    pageNumber: page,
                    limit: limit
                  }
                  fetchContactPerson(payload)
                }
              })
            }}
          />
        </div>
        <div>
          <Snackbar
            open={snackbar}
            autoHideDuration={SnackBarHideDuration}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={handleSnackbarClose}
            TransitionComponent={SnackBarTransition}
          >
            <Alert onClose={handleSnackbarClose} severity={severity}>
              {PopUpMessage}
            </Alert>
          </Snackbar>
        </div>
      </>
    )
  }
}

export default ContactPerson
